import React, { useEffect, useRef, useState } from 'react'

import {
  FilterAccordion,
  OptionProps,
  Tooltip,
  useIsTabletOrMobile,
} from '@avantstay/backoffice-core'

import * as S from '../Filters.styles'
import { FiltersSharedProps } from '../Filters.types'

interface SelectBasedFieldProps<OptionValue extends string> extends FiltersSharedProps {
  title: string
  options: OptionProps<OptionValue>[]
  groupId: string
  initialValue?: OptionValue
  shouldShowClearButton?: boolean
  shouldDisableClearButton?: boolean
  inputWidth?: number
  inputTitle?: string
  onChange: (value?: OptionValue) => void
  isSearchable?: boolean
  isDisabled?: boolean
  menuPosition?: 'fixed' | 'absolute'
  className?: string
  placeholder?: string
  disabledMessage?: string
}

export const SelectBasedField = <OptionValue extends string>({
  title,
  groupId,
  initialValue,
  options,
  isAccordionOpen,
  isSidebarFilter,
  inputWidth,
  inputTitle,
  onChange,
  shouldShowClearButton = false,
  shouldDisableClearButton,
  isSearchable,
  isDisabled,
  menuPosition = 'fixed',
  className,
  placeholder,
  disabledMessage,
}: SelectBasedFieldProps<OptionValue>): JSX.Element => {
  const [selectedOption, setSelectedOption] = useState<OptionValue | undefined>(initialValue)

  useEffect(() => {
    setSelectedOption(initialValue)
  }, [initialValue])

  const wrapperRef = useRef<HTMLDivElement>(null)

  const isTabletOrMobile = useIsTabletOrMobile()

  const handleSubmitValue = () => {
    if (selectedOption !== initialValue) {
      onChange(selectedOption)
    }
  }

  useEffect(() => {
    if (!isTabletOrMobile) {
      handleSubmitValue()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption, isTabletOrMobile])

  useEffect(() => {
    if (isTabletOrMobile) {
      handleSubmitValue()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSidebarFilter])

  const handleOnClear = () => {
    onChange(undefined)
    setSelectedOption(undefined)
  }

  const filterValue = options.find(option => option.value === selectedOption)?.label

  const radioOptions = options.map((option, index) => ({
    title: `${option.label}`,
    id: `${option.label}-${option.value}-${index}`,
    value: option.value,
  }))

  return (
    <FilterAccordion
      title={title}
      className={className}
      onClearFilter={handleOnClear}
      ref={wrapperRef}
      filterValue={filterValue}
      isAccordionOpen={isAccordionOpen}
      isSidebarFilter={isSidebarFilter}
      isInlineFilter={!isTabletOrMobile}
      shouldDisableClearButton={shouldDisableClearButton}
      shouldShowClearButton={shouldShowClearButton}
    >
      <Tooltip content={disabledMessage} disabled={!isDisabled} placement="bottom">
        <div>
          {isSidebarFilter || isTabletOrMobile ? (
            <S.RadioFieldGroupStyled
              label={groupId}
              onChange={e => setSelectedOption(e.currentTarget.value as OptionValue)}
              value={selectedOption}
              options={radioOptions}
            />
          ) : (
            <S.SelectField
              menuPosition={menuPosition}
              isSearchable={isSearchable}
              options={options}
              value={options.find(option => option.value === selectedOption)}
              onChange={option => setSelectedOption(option.value as OptionValue)}
              width={inputWidth}
              title={inputTitle}
              isDisabled={isDisabled}
              placeholder={placeholder}
            />
          )}
        </div>
      </Tooltip>
    </FilterAccordion>
  )
}
